import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Memories.css';

/*
 * ==============================================
 * HOW TO ADD PHOTOS (via Claude chat):
 * ==============================================
 *
 * Just tell Claude:
 *   "Add a childhood photo with url https://... and caption 'Me at age 5'"
 *   "Add a family photo of mom and dad"
 *   "Add a school souvenir image"
 *
 * Or place images in public/images/<category>/ and Claude will wire them up.
 *
 * Each category below has an `items` array. Each item has:
 *   - id: unique number
 *   - src: image URL or path (e.g., "/images/childhood/photo1.jpg")
 *   - caption: text description
 *   - year: optional year string
 */

const categories = [
  {
    key: 'childhood',
    label: 'Childhood Photos',
    icon: '🌟',
    description: 'Precious moments from my early years',
    items: [
      // === ADD CHILDHOOD PHOTOS BELOW ===
      // Example:
      // { id: 1, src: '/images/childhood/photo1.jpg', caption: 'My first birthday', year: '2000' },
      // === ADD CHILDHOOD PHOTOS ABOVE ===
    ],
  },
  {
    key: 'family',
    label: 'Family',
    icon: '👨‍👩‍👧‍👦',
    description: 'My parents, siblings, and loved ones',
    items: [
      // === ADD FAMILY PHOTOS BELOW ===
      // Example:
      // { id: 1, src: '/images/family/mom-dad.jpg', caption: 'Mom and Dad', year: '2020' },
      // === ADD FAMILY PHOTOS ABOVE ===
    ],
  },
  {
    key: 'school',
    label: 'School Souvenirs',
    icon: '🎓',
    description: 'Certificates, awards, and school memories',
    items: [
      // === ADD SCHOOL SOUVENIRS BELOW ===
      // Example:
      // { id: 1, src: '/images/school/graduation.jpg', caption: 'Graduation day', year: '2018' },
      // === ADD SCHOOL SOUVENIRS ABOVE ===
    ],
  },
  {
    key: 'souvenirs',
    label: 'My Souvenirs',
    icon: '🏆',
    description: 'Treasures and mementos collected through the years',
    items: [
      // === ADD COLLECTED SOUVENIRS BELOW ===
      // Example:
      // { id: 1, src: '/images/souvenirs/item1.jpg', caption: 'Souvenir from Tokyo', year: '2023' },
      // === ADD COLLECTED SOUVENIRS ABOVE ===
    ],
  },
];

function Memories() {
  const [activeTab, setActiveTab] = useState('childhood');

  const activeCategory = categories.find((c) => c.key === activeTab);

  return (
    <section className="memories section-padding" id="memories">
      <div className="container">
        <motion.div
          className="memories__header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
        >
          <span className="section-label">Memories & Souvenirs</span>
          <h2 className="section-title">
            A lifetime of<br />
            <span className="text-gradient">cherished moments</span>
          </h2>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="memories__tabs"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map((cat) => (
            <button
              key={cat.key}
              className={`memories__tab ${activeTab === cat.key ? 'memories__tab--active' : ''}`}
              onClick={() => setActiveTab(cat.key)}
            >
              <span className="memories__tab-icon">{cat.icon}</span>
              <span className="memories__tab-label">{cat.label}</span>
              {cat.items.length > 0 && (
                <span className="memories__tab-count">{cat.items.length}</span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Active Category Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="memories__content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <p className="memories__description">{activeCategory.description}</p>

            {activeCategory.items.length > 0 ? (
              <div className="memories__grid">
                {activeCategory.items.map((item, i) => (
                  <motion.div
                    key={item.id}
                    className="memories__card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: 0.05 * i }}
                    whileHover={{ y: -6, transition: { duration: 0.3 } }}
                  >
                    <div className="memories__card-image">
                      <img src={item.src} alt={item.caption} loading="lazy" />
                    </div>
                    <div className="memories__card-info">
                      <p className="memories__card-caption">{item.caption}</p>
                      {item.year && (
                        <span className="memories__card-year">{item.year}</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="memories__empty">
                <div className="memories__empty-icon">{activeCategory.icon}</div>
                <h3 className="memories__empty-title">No items yet</h3>
                <p className="memories__empty-text">
                  Tell Claude to add photos here!<br />
                  <em>"Add a photo to my {activeCategory.label.toLowerCase()} with url https://..."</em>
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

export default Memories;
